import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/hooks/useProfile.ts'
import { useWallet } from '@/hooks/useWallet.ts'
import { useSubscription } from '@/hooks/useSubscription.ts'
import { useActivityLog } from '@/hooks/useActivityLog.ts'
import { PlanSelector } from '@/features/subscription/components/PlanSelector.tsx'
import { SubscriptionTier } from '@/services/contract/contract.types.ts'
import { ProfileForm } from './ProfileForm.tsx'
import { cn } from '@/utils/cn.ts'

export function ProfilePage() {
  const { t } = useTranslation()
  const { address } = useWallet()
  const { profile, loading, error, fetchProfile, saveProfile } = useProfile()
  const {
    subscription,
    fetchSubscription,
    loading: subLoading,
    isActive,
    checkActive,
  } = useSubscription()
  const { logActivity } = useActivityLog()
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    fetchProfile()
    fetchSubscription()
    void checkActive()
  }, [fetchProfile, fetchSubscription, checkActive])

  const handleSubmit = async (data: {
    email: string
    name?: string
    phone?: string
    notify_email?: boolean
    notify_sms?: boolean
    notify_push?: boolean
  }) => {
    const result = await saveProfile(data)
    if (result) {
      setSaved(true)
      await logActivity('profile_updated')
      setTimeout(() => setSaved(false), 3000)
    }
  }

  const isSubscriptionActive = isActive === true
  const subscriptionSectionLoading =
    subLoading || (!!address && isActive === null)

  return (
    <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
      <h1 className="text-fg mb-1 text-[1.75rem] font-bold max-md:text-[1.375rem]">
        {t('profile.title')}
      </h1>
      <p className="text-fg-secondary mb-12">{t('profile.subtitle')}</p>

      {saved && (
        <div className="border-success-border bg-success-muted text-success mb-6 rounded-md border p-4 text-sm">
          {t('profile.savedSuccess')}
        </div>
      )}
      {error && (
        <div
          className={cn(
            'mb-6 rounded-md border p-4 text-sm',
            'border-error-border bg-error-muted text-error',
          )}
        >
          {error}
        </div>
      )}

      <ProfileForm
        profile={profile}
        loading={loading}
        onSubmit={handleSubmit}
      />

      <section
        className="border-line bg-surface-card mt-12 rounded-lg border p-6"
        aria-labelledby="subscription-heading"
      >
        <h2
          id="subscription-heading"
          className="text-fg mb-4 text-lg font-semibold"
        >
          {t('profile.subscriptionHeading')}
        </h2>
        {subscriptionSectionLoading ? (
          <p className="text-fg-secondary m-0 text-[0.9375rem]">
            {t('common.loading')}
          </p>
        ) : isSubscriptionActive ? (
          <div className="space-y-6">
            <div className="border-line flex items-center justify-between border-b pb-2">
              <span className="text-fg-secondary text-sm">Plan</span>
              <span className="text-fg font-semibold">
                {subscription!.tier === SubscriptionTier.OneMonth
                  ? '1 Month'
                  : '1 Year'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-fg-secondary text-sm">Expires</span>
              <span className="text-fg font-semibold">
                {new Date(
                  Number(subscription!.expiryTime) * 1000,
                ).toLocaleDateString()}
              </span>
            </div>
            <div className="border-line border-t pt-2">
              <PlanSelector
                onSubscribed={() => {
                  void fetchSubscription()
                }}
                mode="renew"
                tiers={[SubscriptionTier.OneMonth, SubscriptionTier.OneYear]}
              />
            </div>
          </div>
        ) : (
          <PlanSelector
            onSubscribed={() => {
              void fetchSubscription()
            }}
            tiers={[SubscriptionTier.OneMonth, SubscriptionTier.OneYear]}
          />
        )}
      </section>
    </div>
  )
}
